import React, { useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import RentForecastService from '../../services/rentForecastService';
import useIntl from '../../hooks/useIntl';
import messages from './messages';
import { toastr } from 'react-redux-toastr';

const useRentValuesDirectUpdateMutation = (
  organizationId,
  propertyId,
  rentForecastId,
  selectedProposedChangeType,
  updateSnapshotData: boolean = false,
) => {
  const service = useMemo(() => new RentForecastService(), []);
  const intl = useIntl();
  const queryClient = useQueryClient();
  const prevTypeRef = React.useRef(selectedProposedChangeType);
  const prevForecastRef = React.useRef(rentForecastId);

  React.useEffect(() => {
    if (selectedProposedChangeType !== undefined) {
      prevTypeRef.current = selectedProposedChangeType;
    }
    if (rentForecastId !== undefined && rentForecastId !== null) {
      prevForecastRef.current = rentForecastId;
    }
  }, [selectedProposedChangeType, rentForecastId]);

  return useMutation(
    async (vars) => {
      const { values, skipToast = false } = Array.isArray(vars)
        ? { values: vars, skipToast: false }
        : { values: vars.values, skipToast: !!vars.skipToast };
      if (!Array.isArray(values) || !values.length) return;
      const response = await service.updateRentForecast(
        organizationId,
        propertyId,
        rentForecastId,
        JSON.stringify(values),
        updateSnapshotData,
      );
      return { skipToast, response };
    },
    {
      onSuccess: (data, vars) => {
        const payload = Array.isArray(vars) ? vars : vars.values ?? [];

        const listKey = ['useRentForecastQuery', organizationId, propertyId];
        queryClient.setQueryData(listKey, (old) => {
          if (!old || !Array.isArray(old.floorPlans)) return old;

          const byId = Object.fromEntries(
            payload.map((row) => [row.floorPlanId, row]),
          );

          const floorPlans = old.floorPlans.map((fp) => {
            const upd = byId[fp.id];
            if (!upd) return fp;

            return {
              ...fp,
              fpr: fp.fpr.map((rel) =>
                rel.rentForecastId === rentForecastId
                  ? { ...rel, ...upd }
                  : rel,
              ),
            };
          });

          return { ...old, floorPlans };
        });
        const proposalTypeChanged =
          selectedProposedChangeType !== prevTypeRef.current;

        const forecastChanged = rentForecastId !== prevForecastRef.current;

        prevTypeRef.current = selectedProposedChangeType;
        prevForecastRef.current = rentForecastId;

        if (!data?.skipToast && !proposalTypeChanged && !forecastChanged) {
          toastr.success(
            intl.formatMessage(messages.success),
            intl.formatMessage(messages.updatedForecast),
          );
        }
      },
      onError: (err) =>
        toastr.error('Error', err.toString(), {
          timeOut: 0,
          showCloseButton: true,
        }),
    },
  );
};

export default useRentValuesDirectUpdateMutation;
